import React, { FC, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import './onboarding-testing.scss';

interface AccessResponse {
  success: boolean;
  phoneNumber: string;
}

interface AccountDeletionResult {
  userId: string;
  name: string | null;
  companyId: number | null;
  companyName: string | null;
  companyDeleted: boolean;
  status: 'deleted' | 'would-delete' | 'not-found' | 'failed';
  error?: string;
  auth0Error?: string;
}

interface ResetResponse {
  success: boolean;
  phoneNumber: string;
  userIds: string[];
  accounts: AccountDeletionResult[];
  onboardingSessionsDeleted: number;
  whatsappNumbersDeleted: number;
}

const OnboardingTesting: FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResetResponse | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<AccessResponse>(
          '/onboarding-testing/access',
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setPhoneNumber(res.data.phoneNumber);
        setHasAccess(true);
      } catch (err: any) {
        if (err?.response?.status === 403) {
          setHasAccess(false);
        } else {
          setError('Failed to check access');
        }
      }
    })();
  }, [getAccessTokenSilently]);

  const runReset = useCallback(async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.post<ResetResponse>(
        '/onboarding-testing/reset',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to reset test account');
    } finally {
      setRunning(false);
      setConfirming(false);
    }
  }, [getAccessTokenSilently]);

  if (hasAccess === false) {
    return (
      <div className='onboarding-testing'>
        <div className='access-denied'>Access denied.</div>
      </div>
    );
  }

  return (
    <div className='onboarding-testing'>
      <div className='header'>
        <h2>Onboarding Testing</h2>
      </div>

      {error && <div className='msg error'>{error}</div>}

      {hasAccess && (
        <div className='section'>
          <p className='description'>
            Deletes the onboarding test account for{' '}
            <strong>{phoneNumber}</strong> — the user, its company (if no one
            else is in it), the Auth0 login and the WhatsApp onboarding session
            — so the number can be onboarded again from scratch.
          </p>

          {!confirming ? (
            <button
              className='btn danger'
              disabled={running}
              onClick={() => setConfirming(true)}
            >
              Delete test account
            </button>
          ) : (
            <div className='confirm'>
              <span>Really delete the account for {phoneNumber}?</span>
              <button
                className='btn danger'
                disabled={running}
                onClick={runReset}
              >
                {running ? 'Deleting…' : 'Yes, delete it'}
              </button>
              <button
                className='btn'
                disabled={running}
                onClick={() => setConfirming(false)}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className='section result'>
          <div className='msg success'>
            {result.accounts.length === 0
              ? `No account found for ${result.phoneNumber}.`
              : `${result.accounts.filter((a) => a.status === 'deleted').length} of ${result.accounts.length} account(s) deleted.`}{' '}
            {result.onboardingSessionsDeleted} onboarding session(s) and{' '}
            {result.whatsappNumbersDeleted} WhatsApp number row(s) removed.
          </div>
          {result.accounts.map((a) => (
            <div key={a.userId} className={`account ${a.status}`}>
              <strong>{a.name || '(no name)'}</strong> <small>{a.userId}</small>
              {' — '}
              {a.status}
              {a.companyName &&
                ` · company "${a.companyName}" ${a.companyDeleted ? 'deleted' : 'kept'}`}
              {a.error && <div className='detail error'>{a.error}</div>}
              {a.auth0Error && (
                <div className='detail error'>Auth0: {a.auth0Error}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OnboardingTesting;
